import React from 'react';
import { data } from 'hudl-base';
import { getConsentState } from '../../utility/consent-utils';
import { subscribeToOneTrustBannerHeight } from '../../utility/onetrust-banner-observer';
import './freestar-ad.scss';

// Reusable Freestar sticky-footer ad. Any profile page (athlete, team, author)
// can render it by passing the placement's Freestar name; the component owns the
// slot's full lifecycle so the logic lives in one place rather than being copied
// per page. Freestar sanctions this unit as a Custom Sticky Footer — a standard
// placement the page pins to the footer, not the separate on-call
// hudl-com_sticky_footer product. Sizes are configured on the placement in the
// Freestar dashboard (Freestar tuned them for responsive/CLS).
class FreestarAd extends React.Component {
    static propTypes = {
        // Freestar placement name, used as both the placement and the slot/DOM id.
        placementName: React.PropTypes.string.isRequired,
    };

    constructor(props) {
        super(props);

        this.state = { bannerOffset: 0 };
    }

    componentDidMount() {
        // Register the sticky-footer slot once pubfig.js is ready.
        // Dynamically-rendered (React) slots must use newAdSlots(); the head
        // code's initCallback only handles slots present at initial load.
        if (typeof window !== 'undefined' && window.freestar) {
            window.freestar.queue = window.freestar.queue || [];
            window.freestar.queue.push(() => {
                // Deny by default, allow after consent — the same model setup-dfp.js
                // and display-ad.jsx use for the GPT slots. The slot starts
                // non-personalized and only upgrades to personalized ads once the
                // viewer grants targeting consent (OneTrust C0004). Freestar serves
                // its GAM demand through googletag, so setPrivacySettings is the
                // shared lever.
                //
                // The backend can also force non-personalized ads regardless of
                // consent (e.g. GPC / regional / policy — !CanReceivePersonalizedAds).
                // This is a floor: consent can add restrictions but never lift the
                // backend's. Matches backendRequiresNpa in setup-dfp.js.
                const adData = data.get('ads');
                const backendRequiresNpa = adData?.targetingData?.nonPersonalizedAds === 'True';

                const applyFreestarPrivacy = hasTargetingConsent => {
                    if (!window.googletag || !window.googletag.pubads) {
                        return;
                    }
                    // Under-13 viewers get the COPPA floor and never upgrade,
                    // regardless of consent. __mayUpgradeVendorConsent is set by
                    // _ConsentScripts.
                    if (window.__mayUpgradeVendorConsent === false) {
                        if (window.fsprebid) {
                            window.fsprebid.setConfig({ coppa: true });
                        }
                        window.googletag.pubads().setPrivacySettings({
                            tagForAgeTreatment: 1,
                            nonPersonalizedAds: true,
                            restrictDataProcessing: true,
                        });
                        return;
                    }
                    const restricted = backendRequiresNpa || !hasTargetingConsent;
                    window.googletag.pubads().setPrivacySettings({
                        nonPersonalizedAds: restricted,
                        restrictDataProcessing: restricted,
                    });
                };

                // Apply the resolved consent state before the slot's first auction.
                // getConsentState() falls back to no consent, so an unresolved or
                // blocked OneTrust SDK keeps the anonymous default rather than
                // personalizing.
                getConsentState().then(consentState => {
                    applyFreestarPrivacy(consentState.hasTargeting);
                    // newAdSlots takes an array of slot objects, matching the head
                    // code's freestar.newAdSlots(freestar.config.enabled_slots)
                    // contract.
                    window.freestar.newAdSlots([{
                        placementName: this.props.placementName,
                        slotId: this.props.placementName,
                    }]);
                });

                // Keep the slot in sync when the viewer changes their consent later.
                this.handleConsentChange = () => {
                    if (window.__consentState) {
                        applyFreestarPrivacy(window.__consentState.hasTargeting);
                    }
                };
                window.addEventListener('ConsentScriptsReady', this.handleConsentChange);
            });
        }

        // Keep the sticky-footer ad above the OneTrust consent banner by
        // offsetting it by the live banner height.
        this.unsubscribeBannerObserver = subscribeToOneTrustBannerHeight(height => {
            this.setState({ bannerOffset: height });
        });
    }

    componentWillUnmount() {
        if (this.unsubscribeBannerObserver) {
            this.unsubscribeBannerObserver();
        }
        if (this.handleConsentChange) {
            window.removeEventListener('ConsentScriptsReady', this.handleConsentChange);
        }
        // Tear down the Freestar slot so an SPA navigation away doesn't leave a
        // stale registration behind (which would double-register on remount).
        if (typeof window !== 'undefined' && window.freestar) {
            window.freestar.queue = window.freestar.queue || [];
            window.freestar.queue.push(() => {
                window.freestar.deleteAdSlots([this.props.placementName]);
            });
        }
    }

    render() {
        const { placementName } = this.props;

        // The inline offset sits the slot above the OneTrust consent banner (0 when
        // the banner isn't showing); the rest of the sticky-footer placement lives
        // in freestar-ad.scss so Freestar's cls.css can't stretch the slot into a
        // full-width band.
        //
        // Bare inner div with just the slot id — matches Freestar's own React/Vue
        // components. The placement's sizes live in the Freestar dashboard, not on
        // the element. No data-freestar-ad attribute: that declarative marker is a
        // separate integration path and mixing it with newAdSlots risks
        // double-registration.
        return (
            <div className="freestar-sticky-footer" style={{ bottom: this.state.bannerOffset }}>
                <div align="center" id={placementName} />
            </div>
        );
    }
}

export default FreestarAd;
